import type { Element, ElementContent, Root, RootContent } from 'hast';

/**
 * Subtrees whose text must not be wrapped, either because it is invisible or
 * because the block renders from its raw source and would discard the wrapper.
 */
const SKIP_TAGS = new Set(['pre', 'script', 'style', 'title', 'textarea']);

export interface HighlightResult {
  tree: Root;
  matchCount: number;
}

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Wraps every case-insensitive occurrence of `term` in `span.search-hit`.
 *
 * Operates on a copy of the parsed tree so the parse cache stays clean, which
 * means typing in the search box never re-parses the document.
 */
export const highlightMatches = (tree: Root, term: string): HighlightResult => {
  const needle = term.trim();
  if (needle === '') return { tree, matchCount: 0 };

  const clone = structuredClone(tree) as Root;
  const pattern = new RegExp(escapeRegExp(needle), 'gi');
  let matchCount = 0;

  const wrap = (value: string): ElementContent[] => {
    const result: ElementContent[] = [];
    let cursor = 0;

    pattern.lastIndex = 0;
    for (const match of value.matchAll(pattern)) {
      const start = match.index ?? 0;
      if (start > cursor) result.push({ type: 'text', value: value.slice(cursor, start) });

      result.push({
        type: 'element',
        tagName: 'span',
        properties: { className: ['search-hit'], dataMatchIndex: matchCount },
        children: [{ type: 'text', value: match[0] }],
      });
      matchCount += 1;
      cursor = start + match[0].length;
    }

    if (cursor === 0) return [];
    if (cursor < value.length) result.push({ type: 'text', value: value.slice(cursor) });
    return result;
  };

  // Recursion rather than `visit` so whole skipped subtrees are stepped over;
  // a text node's immediate parent is `code`, not the `pre` we need to detect.
  const walk = (node: Root | Element) => {
    const children: RootContent[] = [];

    for (const child of node.children) {
      if (child.type === 'text') {
        const replacement = wrap(child.value);
        if (replacement.length > 0) children.push(...replacement);
        else children.push(child);
        continue;
      }

      if (child.type === 'element') {
        if (!SKIP_TAGS.has(child.tagName)) walk(child);
        children.push(child);
        continue;
      }

      children.push(child);
    }

    node.children = children as never;
  };

  walk(clone);

  return { tree: clone, matchCount };
};

/** Counts occurrences without building a tree; used for cheap previews. */
export const countMatches = (text: string, term: string): number => {
  const needle = term.trim();
  if (needle === '') return 0;
  return (text.match(new RegExp(escapeRegExp(needle), 'gi')) ?? []).length;
};
